import { Fragment, useState } from 'react';
import styled from '@emotion/styled';
import isPropValid from '@emotion/is-prop-valid';
import { ChevronDownIconLight, Css, UnstyledLi, UnstyledUl } from '@/ui';
import { getListedEntryKey } from '../../../../utils/getListedEntryKey';
import { Color } from '../../../../styles/Color';
import { Shadow } from '../../../../styles/Shadow';
import { IMenuItemEntry } from '../../../../contentful/types/IMenuItem';
import SubNavigation from './SubNavigation';
import { RouterLink } from '../../link/RouterLink';
import {
	MainNavigationItemActive,
	MainNavigationItemCommon,
} from '../Navigation.styles';
import { rotate } from '../../../../styles/animation/rotate';
import { Z_INDEX_MAIN_NAV_ITEM } from '../../../../styles/zIndex';
import { getActivePageProps } from '../../../../navigation/getActivePageProps';
import { useCommonData } from '../../../../common-data/useCommonData';
import { getUrlForPageId } from '../../../../utils/getUrlForLink';

interface IMainNavigationItemsProps {
	type: 'primary' | 'secondary';
	items: IMenuItemEntry[];
	showBackground: boolean;
	siteSectionId?: string;
}

export function MainNavigationItems({
	type,
	items,
	showBackground,
	siteSectionId,
}: IMainNavigationItemsProps) {
	const { pathToPageIdMap } = useCommonData();
	const [clickedItem, setClickedItem] = useState<IMenuItemEntry>();
	const [isSubmenuOpen, setIsSubmenuOpen] = useState(false);

	const handleItemClick = (item: IMenuItemEntry) => {
		setClickedItem(item);
		setIsSubmenuOpen(!isSubmenuOpen);
	};

	return (
		<StyledMainNavigationItems
			showBackground={showBackground}
			hasMenuItems={items.length > 0}
			aria-label="main-navigation"
		>
			<StyledMainNavigationItemsList>
				{items.map((item: IMenuItemEntry, index: number) => (
					<Fragment key={getListedEntryKey(item, index)}>
						<StyledMainNavigationListItem>
							{!item.fields.subMenuItems?.length && item.fields.page ? (
								<StyledMainNavigationItemLink
									{...getActivePageProps(item, siteSectionId)}
									href={
										item.fields.page
											? getUrlForPageId(
													item.fields.page.fields.id,
													pathToPageIdMap,
											  )
											: ''
									}
									isActive={isSubmenuOpen && item === clickedItem}
									showBackground={showBackground}
									type={type}
								>
									{item.fields.title}
								</StyledMainNavigationItemLink>
							) : (
								<StyledMainNavigationItem
									onClick={() => handleItemClick(item)}
									isActive={isSubmenuOpen && item === clickedItem}
									showBackground={showBackground}
									variant={type}
								>
									{item.fields.title} <ChevronDownIconLight />
								</StyledMainNavigationItem>
							)}

							{item.fields?.subMenuItems?.length && (
								<SubNavigation
									subMenuItems={item.fields.subMenuItems}
									open={isSubmenuOpen && item === clickedItem}
								/>
							)}
						</StyledMainNavigationListItem>
					</Fragment>
				))}
			</StyledMainNavigationItemsList>
		</StyledMainNavigationItems>
	);
}

const StyledMainNavigationItemsList = styled(UnstyledUl)`
	display: flex;
	align-items: center;
	pointer-events: all;

	> li:last-child {
		button {
			border-radius: ${Css.pxToRem(10)} ${Css.pxToRem(10)} 0px
				${Css.pxToRem(10)};
		}
	}
`;

const StyledMainNavigationListItem = styled(UnstyledLi)`
	display: flex;
	position: relative;
	height: ${Css.pxToRem(40)};
	z-index: ${Z_INDEX_MAIN_NAV_ITEM};

	&:last-child {
		ul {
			border-top-right-radius: 0;
			border-top-left-radius: ${Css.pxToRem(16)};
			right: 0;
		}
	}
`;

const StyledMainNavigationItems = styled.nav<{
	showBackground?: boolean;
	hasMenuItems: boolean;
}>`
	display: flex;
	margin: 0 auto ${Css.pxToRem(8)};
	cursor: default;
	border-radius: ${Css.pxToRem(54)};
	padding: ${Css.pxToRem(8)} ${Css.pxToRem(18)} ${Css.pxToRem(4)};

	${({ showBackground, hasMenuItems }) =>
		showBackground &&
		hasMenuItems &&
		`
			background: ${Color.PrimaryWhite}};
			box-shadow: ${Shadow.Small};
		`}
`;

const StyledMainNavigationItemLink = styled(RouterLink, {
	shouldForwardProp: (props) => isPropValid(props),
})<{
	isActive: boolean;
	showBackground?: boolean;
	type: 'primary' | 'secondary';
}>`
	&:hover {
		color: ${Color.SecondaryBlue1};
	}

	text-decoration: none;

	${({ showBackground, type }) =>
		MainNavigationItemCommon(showBackground, type)};
	${({ isActive }) => isActive && MainNavigationItemActive};
`;

const StyledMainNavigationItem = styled.button<{
	isActive: boolean;
	showBackground?: boolean;
	variant: 'primary' | 'secondary';
}>`
	border: none;
	cursor: pointer;
	background: none;
	${({ showBackground, variant }) =>
		MainNavigationItemCommon(showBackground, variant)}
	${({ isActive }) => isActive && MainNavigationItemActive}

	svg {
		${({ isActive }) => rotate(isActive)}
	}
`;
